import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { ROLE_LABELS, isAdmin } from '../lib/rbac';
import type { AppRole } from '../lib/database.types';
import {
  DEFAULT_PASSWORD,
  createUser,
  deleteUser,
  listUsers,
  resetPassword,
  setUserRoleAreas,
  type AdminUserRow,
} from '../lib/admin';
import { loadManagers, setManager } from '../lib/team';

const ROLES: AppRole[] = ['editor', 'viewer', 'gestor', 'admin'];
const ROLE_HINT: Record<AppRole, string> = {
  editor: 'Edita as etapas/blocos das áreas escolhidas abaixo.',
  viewer: 'Vê tudo, não edita nada (visitante).',
  gestor: 'Edita tudo (PMO) — não gerencia usuários.',
  admin: 'Edita tudo e gerencia usuários.',
};

export function Usuarios() {
  const { currentOrgId, areas, grants } = useAuth();
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [managers, setManagers] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // formulário de novo usuário
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<AppRole>('editor');
  const [areaKeys, setAreaKeys] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  // edição de usuário existente
  const [editId, setEditId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<AppRole>('editor');
  const [editAreas, setEditAreas] = useState<string[]>([]);

  const areaNameByKey = useMemo(
    () => Object.fromEntries(areas.map((a) => [a.key, a.name])),
    [areas],
  );

  async function refresh() {
    if (!currentOrgId) return;
    setLoading(true);
    try {
      const [us, mg] = await Promise.all([listUsers(currentOrgId), loadManagers(currentOrgId)]);
      setUsers(us);
      setManagers(mg);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  if (!grants || !isAdmin(grants)) {
    return (
      <>
        <div className="topbar">
          <h1>Usuários</h1>
        </div>
        <div className="content">
          <div className="card card-pad">
            <p className="muted">Apenas administradores podem gerenciar usuários.</p>
          </div>
        </div>
      </>
    );
  }

  const toggle = (list: string[], k: string) =>
    list.includes(k) ? list.filter((x) => x !== k) : [...list, k];

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!currentOrgId) return;
    setErr(null);
    setOk(null);
    setBusy(true);
    try {
      await createUser({
        orgId: currentOrgId,
        email: email.trim(),
        role,
        areaKeys: role === 'editor' ? areaKeys : [],
        fullName: fullName.trim() || undefined,
      });
      setOk(
        `Usuário ${email.trim()} criado. Senha provisória "${DEFAULT_PASSWORD}" — ` +
          `ele definirá a dele no primeiro acesso.`,
      );
      setEmail('');
      setFullName('');
      setRole('editor');
      setAreaKeys([]);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(u: AdminUserRow) {
    setEditId(u.user_id);
    setEditRole(u.role);
    setEditAreas(u.area_keys);
    setOk(null);
    setErr(null);
  }

  async function saveEdit(u: AdminUserRow) {
    if (!currentOrgId) return;
    setErr(null);
    try {
      await setUserRoleAreas(
        currentOrgId,
        u.user_id,
        editRole,
        editRole === 'editor' ? editAreas : [],
      );
      setEditId(null);
      setOk(`Acesso de ${u.email} atualizado.`);
      await refresh();
    } catch (e) {
      setErr(`Não foi possível salvar: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function onReset(u: AdminUserRow) {
    if (!currentOrgId) return;
    if (!confirm(`Redefinir a senha de ${u.email} para "${DEFAULT_PASSWORD}"?`)) return;
    setErr(null);
    setOk(null);
    try {
      await resetPassword(currentOrgId, u.user_id);
      setOk(`Senha de ${u.email} redefinida para "${DEFAULT_PASSWORD}".`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function onDelete(u: AdminUserRow) {
    if (!currentOrgId) return;
    if (!confirm(`Remover o acesso de ${u.email}? Esta ação não pode ser desfeita.`)) return;
    setErr(null);
    setOk(null);
    try {
      await deleteUser(currentOrgId, u.user_id);
      setOk(`Usuário ${u.email} removido.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function changeManager(u: AdminUserRow, managerId: string) {
    if (!currentOrgId) return;
    const { error } = await setManager(currentOrgId, u.user_id, managerId || null);
    if (error) {
      setErr(`Não foi possível definir o superior: ${error}`);
      return;
    }
    setManagers((m) => ({ ...m, [u.user_id]: managerId || null }));
    setOk(`Superior de ${u.email} atualizado.`);
  }

  function AreaChips({ selected, onToggle }: { selected: string[]; onToggle: (k: string) => void }) {
    return (
      <div className="chip-toggle" style={{ marginTop: 8, flexWrap: 'wrap' }}>
        {areas.map((a) => (
          <button
            key={a.key}
            type="button"
            className={selected.includes(a.key) ? 'on' : ''}
            onClick={() => onToggle(a.key)}
          >
            {a.name}
          </button>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <h1>Usuários</h1>
      </div>
      <div className="content">
        {/* Novo usuário */}
        <div className="card card-pad">
          <div className="section-title">
            <h2>Convidar novo usuário</h2>
          </div>
          <p className="muted" style={{ marginTop: -4, marginBottom: 12, fontSize: 12.5 }}>
            Escolha o <b>tipo de acesso</b> e, para editores, marque <b>uma ou mais áreas</b> que a
            pessoa poderá editar (ex.: Comercial + Custos).
          </p>
          <form onSubmit={add}>
            <div className="filters" style={{ alignItems: 'flex-end' }}>
              <div className="field" style={{ marginBottom: 0, minWidth: 240 }}>
                <label>E-mail</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="pessoa@empresa.com"
                  autoComplete="off"
                />
              </div>
              <div className="field" style={{ marginBottom: 0, minWidth: 200 }}>
                <label>Nome (opcional)</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Nome da pessoa"
                  autoComplete="off"
                />
              </div>
              <div className="field" style={{ marginBottom: 0, minWidth: 180 }}>
                <label>Tipo de acesso</label>
                <select value={role} onChange={(e) => setRole(e.target.value as AppRole)}>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <p className="muted" style={{ marginTop: 10, fontSize: 12.5 }}>{ROLE_HINT[role]}</p>

            {role === 'editor' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)' }}>
                  Áreas que este usuário pode editar
                </label>
                <AreaChips selected={areaKeys} onToggle={(k) => setAreaKeys((s) => toggle(s, k))} />
              </div>
            )}

            <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="btn-primary" disabled={busy}>
                {busy ? 'Criando…' : 'Adicionar usuário'}
              </button>
              <span className="muted" style={{ fontSize: 12.5 }}>
                Senha provisória: <b>{DEFAULT_PASSWORD}</b> (troca obrigatória no 1º acesso)
              </span>
            </div>
          </form>
          {err && <p className="err">{err}</p>}
          {ok && <p className="ok">{ok}</p>}
        </div>

        {/* Lista */}
        <div className="card card-pad">
          <div className="section-title">
            <h2>Usuários da organização</h2>
            <button className="btn-ghost btn-sm" onClick={() => void refresh()} disabled={loading}>
              {loading ? 'Atualizando…' : 'Atualizar'}
            </button>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>E-mail</th>
                  <th>Nome</th>
                  <th>Acesso</th>
                  <th>Áreas que edita</th>
                  <th>Superior</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const editing = editId === u.user_id;
                  return (
                    <tr key={u.user_id}>
                      <td>{u.email}</td>
                      <td>{u.full_name ?? '—'}</td>
                      <td>
                        {editing ? (
                          <select
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value as AppRole)}
                            style={{ fontSize: 12.5 }}
                          >
                            {ROLES.map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABELS[r]}
                              </option>
                            ))}
                          </select>
                        ) : (
                          ROLE_LABELS[u.role]
                        )}
                      </td>
                      <td className="muted">
                        {editing ? (
                          editRole === 'editor' ? (
                            <AreaChips
                              selected={editAreas}
                              onToggle={(k) => setEditAreas((s) => toggle(s, k))}
                            />
                          ) : (
                            <span>todas</span>
                          )
                        ) : u.role === 'editor' ? (
                          u.area_keys.length
                            ? u.area_keys.map((k) => areaNameByKey[k] ?? k).join(', ')
                            : '—'
                        ) : (
                          'todas'
                        )}
                      </td>
                      <td>
                        <select
                          value={managers[u.user_id] ?? ''}
                          onChange={(e) => void changeManager(u, e.target.value)}
                          style={{ maxWidth: 160, fontSize: 12.5 }}
                        >
                          <option value="">— sem superior —</option>
                          {users
                            .filter((x) => x.user_id !== u.user_id)
                            .map((x) => (
                              <option key={x.user_id} value={x.user_id}>
                                {x.full_name ?? x.email}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        {u.must_change_password ? (
                          <span className="badge atencao">Senha provisória</span>
                        ) : (
                          <span className="badge concluido">Ativo</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {editing ? (
                          <>
                            <button className="btn-primary btn-sm" onClick={() => void saveEdit(u)}>
                              Salvar
                            </button>
                            <button className="btn-ghost btn-sm" onClick={() => setEditId(null)}>
                              Cancelar
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn-ghost btn-sm" onClick={() => startEdit(u)}>
                              Editar áreas
                            </button>
                            <button className="btn-ghost btn-sm" onClick={() => void onReset(u)}>
                              Redefinir senha
                            </button>
                            <button className="btn-ghost btn-sm" onClick={() => void onDelete(u)}>
                              Remover
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      Nenhum usuário ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
